import { createContext, JSX, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { TranslationArgs, TranslationArgsWithJSX, TranslationKey } from './translations';
import get from 'lodash/get';
import has from 'lodash/has';
import { SessionStorageService } from '@/services/session-storage';
import { useAsync } from '@/hooks/use-async';
import { useLoggedUser } from '@/contexts/current-user-context';
import { useGetPatient } from '@/api/get-personal-info';
import { useEncounter } from '@/contexts/encounter-context';

function translation(translations: TranslationsObject) {
  // Function implementation. Extracted here so that we can have overloads
  // and only call useCallback on the "result"
  function t<K extends TranslationKey>(key: K, ...params: TranslationArgs<K>): string;
  function t<K extends TranslationKey>(key: K, ...params: TranslationArgsWithJSX<K>): ReactNode;
  function t<K extends TranslationKey>(
    key: K,
    ...params: TranslationArgs<K> | TranslationArgsWithJSX<K>
  ) {
    const translation = get(translations, key);
    if (!translation) throw new Error(`Translation key ${key} not found`);
    if (params.length === 0) return translation;

    const p = params[0];

    const result = translation.split(/(%\{.+?\})/).map((word: string) => {
      if (word.startsWith('%{') && word.endsWith('}')) {
        const param = word.slice(2, -1) as keyof typeof p;
        if (param in p) {
          if (!isNumberOrString(p[param])) {
            assertHasKey(p[param] as JSX.Element);
          }
          return p[param];
        }
      }
      return word;
    });

    if (result.every((word: string | number | JSX.Element) => isNumberOrString(word))) {
      return result.join('');
    }

    return <>{result}</>;
  }

  function isTranslationKey(key: string): key is TranslationKey {
    return has(translations, key);
  }

  return { t, isTranslationKey };
}

type TranslationFn = ReturnType<typeof translation>;

const locales = [
  {
    key: 'en',
    label: 'English',
  },
] as const;

type SupportedLocale = (typeof locales)[number];

type SetLocale = (locale: SupportedLocale['key']) => unknown;

interface I18nState {
  setLocale: SetLocale;
  locale: SupportedLocale;
  locales: typeof locales;
  t: TranslationFn['t'];
  baseT: TranslationFn['t'];
  isTranslationKey: TranslationFn['isTranslationKey'];
  loading: boolean;
}

export type TranslationFunction = TranslationFn['t'];

const CHOSEN_LANGUAGE_KEY = 'chosen_language';

const storage = new SessionStorageService<string>(CHOSEN_LANGUAGE_KEY);

function isNumberOrString(element: string | number | JSX.Element): element is string | number {
  return ['string', 'number'].includes(typeof element);
}

function assertHasKey(element: JSX.Element) {
  if (!element.key) {
    throw new Error(`Element key is not defined`);
  }
}

type TranslationsObject = Record<TranslationKey, string> | undefined;

const I18nContext = createContext<I18nState | null>(null);

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [locale, setLocale] = useState<SupportedLocale>(
    () => locales.find(({ key }) => key === storage.data) ?? locales[0],
  );
  const translationFunctions = useRef<TranslationFn | undefined>(undefined);

  const setLocaleCallback = useCallback(
    (newLocale: SupportedLocale['key']) => {
      setLocale((currentLocale) => {
        if (newLocale !== currentLocale.key) {
          storage.save(newLocale);
        }

        return locales.find((l) => l.key === newLocale) ?? locales[0];
      });
    },
    [setLocale],
  );

  const { loading, error } = useAsync(async () => {
    const localeSpecificTranslations = (await import(`./translations/${locale.key}/index.ts`)) as {
      translations: Record<TranslationKey, string>;
    };
    translationFunctions.current = translation(localeSpecificTranslations.translations);
  }, [locale]);

  if (loading) {
    return null;
  }

  if (error || !translationFunctions.current) {
    throw new Error(`Could not load translations for locale ${locale.label}`);
  }

  return (
    <I18nContext.Provider
      value={{
        setLocale: setLocaleCallback,
        locale,
        t: translationFunctions.current.t,
        baseT: translationFunctions.current.t,
        isTranslationKey: translationFunctions.current.isTranslationKey,
        locales,
        loading,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within a I18nProvider');
  }
  return context;
}

function toSurrogateKey(key: TranslationKey): string {
  return `${key}Surrogate`;
}

export function getKey<K extends TranslationKey>(
  key: K,
  isSurrogate: boolean,
  isTranslationKey: (key: string) => key is TranslationKey,
): K {
  if (!isSurrogate) return key;

  const surrogateKey = toSurrogateKey(key);

  return isTranslationKey(surrogateKey) ? (surrogateKey as K) : key;
}

type SurrogateI18nSource = 'patient' | 'encounter';

interface SurrogateI18nProviderProps {
  source: SurrogateI18nSource;
  children: ReactNode;
}

export function SurrogateI18nProvider({ source, children }: SurrogateI18nProviderProps) {
  if (source === 'patient') {
    return <PatientSurrogateI18nProvider>{children}</PatientSurrogateI18nProvider>;
  }

  return <EncounterSurrogateI18nProvider>{children}</EncounterSurrogateI18nProvider>;
}

function PatientSurrogateI18nProvider({ children }: { children: ReactNode }) {
  const { user } = useLoggedUser();
  const { data: patient, isLoading } = useGetPatient(user.id);

  if (isLoading) {
    return null;
  }

  return (
    <SurrogateAwareI18nProvider isSurrogate={Boolean(patient?.surrogate)}>
      {children}
    </SurrogateAwareI18nProvider>
  );
}

function EncounterSurrogateI18nProvider({ children }: { children: ReactNode }) {
  const { encounter } = useEncounter();

  return (
    <SurrogateAwareI18nProvider isSurrogate={Boolean(encounter.isSurrogate)}>
      {children}
    </SurrogateAwareI18nProvider>
  );
}

interface SurrogateAwareI18nProviderProps {
  isSurrogate: boolean;
  children: ReactNode;
}

function SurrogateAwareI18nProvider({ isSurrogate, children }: SurrogateAwareI18nProviderProps) {
  const parent = useTranslation();

  const t = useCallback(
    <K extends TranslationKey>(key: K, ...params: TranslationArgsWithJSX<K>) =>
      parent.baseT(getKey(key, isSurrogate, parent.isTranslationKey), ...params),
    [parent, isSurrogate],
  ) as TranslationFunction;

  return <I18nContext.Provider value={{ ...parent, t }}>{children}</I18nContext.Provider>;
}
